import { TEAM_NAMES, ARCHETYPES, MODIFIERS, MOCK_ROSTER_SIZE } from '../data/archetypes';
import { NFL_TEAMS, PLAYERS, UNIQUE_PLAYERS } from '../data/players';
import { getTierForRank, generatePersonalTierSheet } from '../data/tiers';
import { calcAiMaxBidV2, calcAiOpeningBid, pickAiNomination, isDraftable, _traceNote, _traceWants, _sflexDials, _qbTail } from './auction';
import { getEffectivePosRank, parseRankNum } from '../data/adpData';
import { buildRosterSlots } from '../utils/rosterSlotFill';
import { tierForPlayer, tierMidDollars } from '../utils/roomTemp';
import { isQbPremiumFormat } from '../utils/leagueFormat';

// Lives in utils/rosterSlotFill.js alongside fillRosterSlots so consumers that
// need slots without the bid engine (api/email/results.js) can import it from a
// dependency-free module. Re-exported here so existing import sites are unchanged.
export { buildRosterSlots };

// ── Demand-side dials (chunk 5 part 2) — PROBE ONLY ─────────────────
// Two knobs on the DEMAND side of the top of the board, each with today's
// behaviour as a point on it. No shipped src/hooks, src/components or
// src/engine path writes this object. Two things write it: the diagnostic
// harnesses, and utils/devDials.js (the dev-only feel-pass activation, which
// is stripped from production builds).
//
//   rscScale — scales `profile.rbSpendCap` for the seats that carry one (the
//     three WR Heavy at the 10-team default, $60 each). 1 = identity. Infinity
//     writes 0, which is the engine's own "no cap" sentinel (auction.js:856
//     gates on `> 0`), so the gate is removed rather than raised to a huge
//     number that later `Math.min`s would still touch.
//   rbShift — moves `rbShift x wr$` from wr$ to rb$ on every seat BEFORE the
//     renormalization at :224-233. The per-seat raw sum is unchanged by
//     construction, so every scale factor `s`, and therefore qb$ and te$, are
//     untouched. 0 = identity.
//
// Both guards test for the identity value and fall through to the original
// expression, so the default path is bit-identical rather than merely equal.
export const _demandDials = { rscScale: 1, rbShift: 0 };

// ── Canonical room rule (chunk 8, SHIPPED chunk 9 — default ON) ─────
// BEFORE this rule the opponent pool was NAME-DEPENDENT: it filtered the user's
// display name out of TEAM_NAMES and then sliced to teamCount-1. When the
// user's name IS a built-in TEAM_NAME the filter dropped that name's seat; when
// it was NOT, the filter dropped nothing and the slice dropped the LAST
// configured name instead. Those are different rooms with different archetype
// multisets, and which one a user got depended on whether they had renamed their
// team — a calibrated league renames it automatically (useMockDraft.js:471), so
// half the corpus was already on the second room.
//
// `canonical: true` makes the pool always the first teamCount-1 TEAM_NAMES, so
// every user gets one room regardless of display name: the user always displaces
// the LAST configured seat (The Hammer at 10, Zero RB at 12, Balanced at 14).
// That is the old custom-name behaviour, formalized and made name-independent.
//
// SHIPPED DEFAULT since chunk 9 (Aug 8, 2026). The flag object is KEPT FOR ONE
// RELEASE as a kill switch — `canonical: false` still takes the pre-chunk-9
// expression verbatim, so the old behaviour is bit-identical rather than merely
// equal. FOLLOW-UP: remove the flag and the dead branch next release.
//
// The userSlot = -1 path never reaches either branch (validUserSlot is false),
// so batch sim and every all-AI probe are untouched at BOTH flag settings — the
// identity digests prove it, which is why flipping this default cannot move the
// ship digest.
//
// Measured before shipping (chunk 8): 52.7% of real drafts already ran this
// room; the flip changes the room for the 47.3% who kept the default team name,
// concentrated at 10 teams. At the accepted cell the bundle re-clears all three
// MUST rows in this room (65/58/37) — it does NOT in the other one (1/3).
export const _roomRule = { canonical: true };

/**
 * Universal value recognition — prevents elite-player steals when the standing bid is far
 * below the player's value. Boosts existing low bids AND adds real bids for teams that
 * hard-passed (calcAiMaxBidV2 returned 0), so a player cannot sell at the nominator's own
 * opening price just because every archetype happened to pass.
 *
 * Extracted Aug 5 2026 from the inline block in simulateDraftSync, which was the ONLY place
 * it existed — the live interactive loop in useMockDraft.js had no equivalent, so a $32-board
 * TE could sell for $5 at pick 11 in the browser while batch sim never reproduced it.
 * Both loops now call this. Mutates willingBidders in place.
 *
 * recheckMaxBid(team, emptySlots, teamCaps) -> number is supplied by each caller, because the
 * two loops hold calcAiMaxBidV2's 18 arguments in differently-named locals.
 *
 * skipIndex is the human's seat when he is bidding manually — never auto-bid on his behalf.
 * Pass -1 (the default) when every seat is AI-controlled, e.g. batch sim and auto-finish.
 *
 * The anchor is the BOARD price (dynamicPrices), not player.value. players.js `value` is a
 * stale static field — it still has McBride as TE1 over Bowers, and the Aug board raise
 * widened the gap without touching it. Anchoring there protected Bowers to $18 against a
 * $40 board, and left anything with value < 25 (Loveland, Kraft) with no floor at all in
 * any loop. priceMap is keyed `${name}-${pos}` (usePricing.js) — a bare-name lookup misses
 * silently and falls back to the stale value, which looks identical to working.
 *
 * The gate is a DOLLAR amount (25 = 12.5% of a $200 budget), so it must scale with the
 * league's starting budget or it protects a different slice of the board in every format.
 * The 0.65 / 0.70 multipliers are ratios and are correctly unitless — do not scale those.
 */
export function applyValueRecognitionFloor({
  player, currentBid, excludeIndex, skipIndex = -1, willingBidders, teams, rosterSize, recheckMaxBid,
  priceMap = null, budget = 200,
}) {
  const anchor = priceMap?.get(`${player.name}-${player.pos}`) ?? player.value;
  const gate = 25 * (budget / 200);
  if (!(anchor >= gate) || !(currentBid < anchor * 0.65)) return;
  const floorBid = Math.round(anchor * 0.70);

  willingBidders.forEach(wb => {
    if (wb.id === excludeIndex || wb.id === skipIndex) return;
    // A seam-B (_qbTail.benchQbBid) seat is in this list at a deliberate cap and
    // is not a seat that wants the player — boosting it to 0.70 x board would
    // walk straight through that cap. The flag only exists when the seam is on,
    // so this is inert at the default state and in every 1QB room.
    if (wb.benchQb) return;
    const team = teams[wb.id];
    if (!team?.profile) return;
    const emptySlots = rosterSize - team.roster.length;
    if (emptySlots <= 0 || team.budget < 15) return;
    if (wb.maxBid >= floorBid) return;
    const hardCap = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
    const boostedBid = Math.min(floorBid, hardCap);
    if (boostedBid > wb.maxBid) wb.maxBid = boostedBid;
  });

  Object.entries(teams).forEach(([id, team]) => {
    const i = parseInt(id);
    if (i === excludeIndex || i === skipIndex) return;
    if (!team?.profile) return;
    const emptySlots = rosterSize - team.roster.length;
    if (emptySlots <= 0 || team.budget < 15) return;
    if (willingBidders.find(wb => wb.id === i)) return;
    const teamCaps = { maxDef: team.maxDef, maxQB: team.maxQB, maxTE: team.maxTE };
    if (recheckMaxBid(team, emptySlots, teamCaps) !== 0) return;
    const hardCap = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
    const realBid = Math.min(floorBid, hardCap);
    if (realBid > currentBid) willingBidders.push({ id: i, maxBid: realBid });
  });
}

export function getFlexPositions(positions) {
  const flex = ["RB", "WR", "TE"];
  return flex;
}

export function getSflexPositions() {
  return ["QB", "RB", "WR", "TE"];
}

/**
 * WHO DRAFTS: the opponent seats, as exactly one derivation.
 *
 * Exactly teamCount-1 opponent names. Under `_roomRule.canonical` the pool is
 * name-independent — always the first teamCount-1 names — so the user displaces
 * the LAST configured seat whatever they call themselves. See _roomRule for why.
 * Off the canonical rule it drops userOwner by name, and the trailing slice
 * matters: once userOwner is a real manager name rather than a TEAM_NAME the
 * filter removes nothing and the list is one too long.
 *
 * ⚠️ THIS IS THE ONLY PLACE THE RULE EXISTS, and it has to stay that way.
 * `buildDraftTeams` below calls it rather than inlining it, and every UI that
 * asks "which seats are there?" imports it — the expert panel, the keeper seat
 * picker, member-name seeding, profile fitting. It used to live in useMockDraft
 * with a comment telling the next person to keep it in step with the copy in
 * buildDraftTeams, and a THIRD copy in MockSetup did not get the memo: it
 * skipped the canonical rule, so under a display-name collision the expert
 * panel offered a seat that never drafted and hid one that did. One function,
 * called from everywhere, is the fix — a comment asking for agreement is not a
 * mechanism.
 *
 * Returns a FRESH array every call; buildDraftTeams shuffles it in place.
 *
 * Lives here, next to its only structural caller, so importing it does not drag
 * useMockDraft — and the sound engine behind it — into a setup screen.
 */
export function opponentSeats(teamCount, userOwner) {
  if (_roomRule.canonical) return TEAM_NAMES.slice(0, teamCount - 1);
  return TEAM_NAMES.slice(0, teamCount).filter(n => n !== userOwner).slice(0, teamCount - 1);
}

export function buildDraftTeams(userOwner, userSlot, teamConfigs, teamCount, budget = 200, userTierOverrides = null, skipShuffle = false, settings = {}) {
  const teams = {};
  const teamNames = TEAM_NAMES.slice(0, teamCount);
  const validUserSlot = userSlot >= 0 && userSlot < teamCount;
  // The shared pool — see opponentSeats directly above. With no valid user slot
  // there is no seat to displace, so every configured name drafts.
  const otherOwners = validUserSlot ? opponentSeats(teamCount, userOwner) : [...teamNames];
  if (!skipShuffle) {
    for (let i = otherOwners.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [otherOwners[i], otherOwners[j]] = [otherOwners[j], otherOwners[i]];
    }
  }
  const aiSlots = Array.from({ length: teamCount }, (_, i) => i).filter(i => i !== userSlot);
  const extraDefSlot = aiSlots[Math.floor(Math.random() * aiSlots.length)];
  const allSlots = Array.from({length: teamCount}, (_, i) => i);
  for (let i = allSlots.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allSlots[i], allSlots[j]] = [allSlots[j], allSlots[i]];
  }
  // ── QB roster targets ────────────────────────────────────────────────
  // The 50/40/10 split over 1/2/3 quarterbacks below is a 1QB-LEAGUE
  // distribution, and until aug14 it was applied blind to sflexCount. In a
  // 2-QB-starter format that hands half the room a `maxQB` of 1, and the
  // bidder-selection gate (simulation.js `pos === "QB" && pCount >= maxQB`,
  // mirrored in useMockDraft/liveLoopSim) drops such a seat from every QB
  // auction the moment it owns one QB — upstream of calcAiMaxBidV2, so no
  // bid-side seam can reach it. Measured: 100% of the seats that finished a
  // superflex draft with one QB carried maxQB <= 1
  // (docs/data/sflex-qb2-mustfill-aug14.md).
  //
  // The fix is settings-derived: shift the distribution up by the starter
  // requirement instead of anchoring it at 1. The 1QB branch is the original
  // expression VERBATIM, so that path is bit-identical rather than merely equal
  // (same rule as every other dial).
  //
  // TOP OF THE RANGE — `maxQbTopOffset`, 1 shipped. The first pass (aug14) kept
  // the 1QB table's 50/40/10 SHAPE, giving 2/3/4, and that regressed the deep
  // room: 10% of seats could legally roster FOUR quarterbacks in a 2-QB format,
  // 4-QB rosters went 31 -> 131, and the extra demand squeezed a tail out of the
  // market entirely — scorecard-arm one-QB 1.33% -> 5.17% with four ZERO-QB
  // seats. The floor was right; the ceiling was not. 50/50 over
  // requirement/requirement+1 (2/3 in standard superflex) keeps capacity ample
  // — a 12-team room seats a mean of 2.5 QBs against ~2.04 sold per draft — with
  // no 4-QB hoarding channel at all. Offset 2 reproduces the aug14 first pass
  // byte-for-byte, which is what the paired-seed control cell uses.
  const sflexCount = settings?.positions?.SFLEX || 0;
  const qbRequired = (settings?.positions?.QB ?? 1) + sflexCount;
  // Drives the QB STRATEGY assignment below (which ladder a seat rolls on).
  // Deliberately separate from `sflexCount`, which still gates roster-legality
  // and every bidding-side branch — see the NOTE at the maxQB assignment.
  const qbPremium = isQbPremiumFormat(settings?.positions);
  // The gate's SCOPE. `sflexCount > 0` was the aug14 scope and it leaves a
  // true-2QB roster (QB:2, SFLEX:0) on the legacy ladder — see the NOTE at the
  // maxQB assignment below. `maxQbGatePremium` (auction.js `_sflexDials`) swaps
  // it for the shared demand predicate. The two agree on every roster except
  // QB>=2, so the OFF arm is the aug14 expression verbatim and every non-2QB
  // digest is unmoved at either setting.
  const qbGate = _sflexDials.maxQbGatePremium ? qbPremium : sflexCount > 0;
  const useQbFix = qbGate && _sflexDials.maxQbFix;
  const topOff = _sflexDials.maxQbTopOffset;
  const qbTargets = {};
  allSlots.forEach((slot, idx) => {
    qbTargets[slot] = useQbFix
      ? qbRequired + (idx < Math.floor(teamCount * 0.5) ? 0 : idx < Math.floor(teamCount * 0.9) ? 1 : topOff)
      : (idx < Math.floor(teamCount * 0.5) ? 1 : idx < Math.floor(teamCount * 0.9) ? 2 : 3);
  });
  const teTargets = {};
  allSlots.forEach((slot, idx) => { teTargets[slot] = idx < Math.floor(teamCount * 0.7) ? 1 : idx < Math.floor(teamCount * 0.9) ? 2 : 3; });
  const shuffledAI = [...aiSlots];
  for (let i = shuffledAI.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [shuffledAI[i], shuffledAI[j]] = [shuffledAI[j], shuffledAI[i]]; }
  // First pass: build slot→ownerKey mapping (post-shuffle) to determine archetypes
  const slotOwners = {};
  let preAiIdx = 0;
  for (let i = 0; i < teamCount; i++) {
    slotOwners[i] = i === userSlot ? userOwner : otherOwners[preAiIdx++];
  }
  // Seats carrying a fitted manager profile (calibrated mocks only — set by
  // applyMemberProfiles in useMockDraft). For these the fit owns the whole
  // profile: no random strategy rolls, no random personalities, and no Patient
  // Spender guarantee, because the fit already found the real ones from that
  // manager's own drafts. Nothing else sets this flag, so batch sim, the deep
  // sim probe, and every uncalibrated league take the original path untouched.
  const fittedSlots = new Set(
    aiSlots.filter(slot => (teamConfigs[slotOwners[slot]] || {}).fittedProfile === true)
  );
  // Seats whose modifier list is taken VERBATIM from the config — no strategy
  // rolls, no Enforcer/Homer draw, no Patient Spender guarantee. MEASUREMENT
  // ONLY (aug29): nothing in the app sets this flag, and no default sets it, so
  // `pinnedSlots` is empty on every shipping path and every condition below
  // evaluates exactly as it did before. It exists because a QB-premium format
  // STRIPS and re-rolls any supplied QB strategy (see the qbPremium block), so
  // an observed room's mix is otherwise inexpressible — a probe that wants to
  // re-run a real browser draft's profiles cannot ask for them.
  // Distinct from `fittedProfile`, which forces the NEUTRAL option on every
  // strategy rather than honouring what it was handed.
  const pinnedSlots = new Set(
    aiSlots.filter(slot => (teamConfigs[slotOwners[slot]] || {}).pinnedProfile === true)
  );
  // Patient Spender guarantee — find Balanced AI teams using post-shuffle mapping.
  // Fitted seats are excluded: the guarantee exists to put 2 Patient Spenders in
  // a randomly-generated room, and it still does that among the UNFITTED seats.
  const psConflicts = ["The Hammer", "Homer"];
  const balancedAISlots = aiSlots.filter(slot => {
    if (fittedSlots.has(slot) || pinnedSlots.has(slot)) return false;
    const cfg = teamConfigs[slotOwners[slot]] || {};
    return (cfg.archetype || "Balanced") === "Balanced" &&
           !(cfg.modifiers || []).some(m => psConflicts.includes(m));
  });
  // Exactly 2 Patient Spenders per draft — shuffle eligible Balanced teams, pick first 2
  const shuffledEligible = [...balancedAISlots];
  for (let i = shuffledEligible.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [shuffledEligible[i], shuffledEligible[j]] = [shuffledEligible[j], shuffledEligible[i]]; }
  const patientSlots = new Set(shuffledEligible.slice(0, 2));
  // Enforcer/Homer — skip Patient Spender slots
  const enforcerSlots = new Set(shuffledAI.filter(i => !patientSlots.has(i) && !fittedSlots.has(i)).slice(0, 1 + Math.floor(Math.random() * 2)));
  const homerSlots = new Set(shuffledAI.filter(i => !patientSlots.has(i) && !fittedSlots.has(i) && !enforcerSlots.has(i)).slice(0, 2 + Math.floor(Math.random() * 2)));
  // Memoized draftable check — isDraftable recomputes pool counts per call, so
  // cache by player name once per buildDraftTeams run (shared across all teams).
  const _draftableCache = new Map();
  const isDraftableMemo = (p) => {
    let v = _draftableCache.get(p.name);
    if (v === undefined) { v = isDraftable(p, settings); _draftableCache.set(p.name, v); }
    return v;
  };
  for (let i = 0; i < teamCount; i++) {
    const ownerKey = slotOwners[i];
    // The user's seat reads an explicit "__user" profile (Auto-Bid / Quick Sim
    // draft for the user with it). "__user" can't collide with a TEAM_NAME and is
    // never an opponent. Batch-sim (userSlot=-1) never hits this branch.
    const cfg = (i === userSlot ? teamConfigs.__user : teamConfigs[ownerKey]) || {};
    const archKey = cfg.archetype || "Balanced";
    const arch = ARCHETYPES[archKey] || ARCHETYPES["Balanced"];
    // User display name always comes from the team-name input (ownerKey), not __user.
    let displayName = (i !== userSlot && cfg.customName?.trim()) || ownerKey;
    // Name collision under the canonical rule. The old filter removed the
    // user's name from the pool, so a user calling themselves "Mike" could never
    // sit next to the AI Mike; the canonical pool keeps every name, so they can.
    //
    // This is NOT cosmetic. Team identity is compared BY NAME in three places —
    // The Hammer's rival census (auction.js:857 `t.name !== myName`), the
    // endgame rival scan (:951) and the commitment ceiling (:1433) — so two
    // seats sharing a name would make each invisible to the other's own check.
    // Disambiguating the AI's LABEL fixes that while leaving `ownerKey` (and so
    // the archetype multiset, the config lookup and every RNG draw) untouched.
    if (_roomRule.canonical && validUserSlot && i !== userSlot &&
        displayName.trim().toLowerCase() === String(userOwner || '').trim().toLowerCase()) {
      displayName = `${displayName} (CPU)`;
    }
    let modifiers = [...(cfg.modifiers || [])];
    // Patient Spender: add to existing modifiers (compatible with Elite QB, Punt TE, etc.)
    if (i !== userSlot && patientSlots.has(i) && !modifiers.includes("Patient Spender")) {
      modifiers.push("Patient Spender");
    }
    const isFitted = i !== userSlot && fittedSlots.has(i);
    // Pinned reads the seat's OWN config, so it covers the user seat too — the
    // browser's Auto-Bid seat takes a QB-premium strategy roll like any other.
    const isPinned = cfg.pinnedProfile === true;
    if (i !== userSlot && !isFitted && !isPinned && !patientSlots.has(i) && modifiers.length <= 1) {
      if (enforcerSlots.has(i)) modifiers.push("Enforcer");
      if (homerSlots.has(i)) modifiers.push("Homer");
    }
    // Assign QB strategy (single-starter formats only — QB-premium formats
    // handle their own below). A fitted seat whose manager showed no QB
    // tendency gets the neutral option rather than a roll: the fit measured no
    // signal there, and a random Elite/Punt would be inventing one.
    // This test and the QB-premium one below are ONE if/else: they must read
    // the same predicate or a 2QB seat rolls a 1QB strategy here and then also
    // takes an SF strategy below. The strip list there does not include
    // "Balanced QB", so such a seat would carry two live QB modifiers.
    if (i !== userSlot && !qbPremium && !isPinned) {
      const qbConflicts = ["Elite QB", "Punt QB", "Balanced QB", "Elite QB SF", "Balanced QB SF", "Punt QB SF"];
      if (!modifiers.some(m => qbConflicts.includes(m))) {
        if (isFitted) modifiers.push("Balanced QB");
        else {
          const r = Math.random();
          if (r < 0.30) modifiers.push("Elite QB");
          else if (r < 0.70) modifiers.push("Balanced QB");
          else modifiers.push("Punt QB");
        }
      }
    }
    // Assign TE strategy to every AI team
    if (i !== userSlot && !isPinned) {
      const teConflicts = ["Elite TE", "Punt TE", "Balanced TE"];
      if (!modifiers.some(m => teConflicts.includes(m))) {
        if (isFitted) modifiers.push("Balanced TE");
        else {
          const r = Math.random();
          if (r < 0.20) modifiers.push("Elite TE");
          else if (r < 0.80) modifiers.push("Balanced TE");
          else modifiers.push("Punt TE");
        }
      }
    }
    // QB-premium (SFLEX or 2QB): assign a random SF QB strategy to every slot
    // (AI and user for simulate-to-end). 2QB joins here because the strategy
    // question it faces is the SF one — two mandatory starters — not the 1QB
    // "do I pay up for the one slot" question.
    if (qbPremium && !isPinned) {
      const qbMods = ["Elite QB", "Punt QB", "Elite QB SF", "Balanced QB SF", "Punt QB SF"];
      const existing = modifiers.filter(m => qbMods.includes(m));
      if (existing.length > 1) console.warn(`[buildDraftTeams] Slot ${i} had duplicate QB mods: ${existing.join(', ')}`);
      modifiers = modifiers.filter(m => !qbMods.includes(m));
      const qbStrats = ["Elite QB SF", "Balanced QB SF", "Punt QB SF"];
      // Fitted seats take the neutral SF strategy instead of a roll. NOTE the
      // filter above strips the 1QB-named Elite/Punt QB that a fit emits, so a
      // fitted QB tendency does not survive into superflex — see the SFLEX
      // caveat; fitMemberProfiles has no SF-aware output today.
      // The identity branch takes the original expression VERBATIM so the
      // default path is bit-identical, not merely equal (see `_sflexDials`).
      // The weighted branch consumes exactly one Math.random() as well, which
      // is what keeps the aug13 sweep's paired seeds aligned across cells.
      let sfStrat;
      if (isFitted) sfStrat = "Balanced QB SF";
      else if (_sflexDials.puntSeatWeight === 1 / 3) sfStrat = qbStrats[Math.floor(Math.random() * 3)];
      else {
        const rs = Math.random();
        sfStrat = rs < 1 / 3 ? "Elite QB SF"
          : rs < 1 - _sflexDials.puntSeatWeight ? "Balanced QB SF"
          : "Punt QB SF";
      }
      modifiers.push(sfStrat);
    }
    let qb$ = arch.qb$, rb$ = arch.rb$, wr$ = arch.wr$, te$ = arch.te$;
    modifiers.forEach(mod => {
      const m = MODIFIERS[mod];
      if (m?.overrides.qb$ !== undefined) qb$ = m.overrides.qb$;
      if (m?.overrides.rb$ !== undefined) rb$ = m.overrides.rb$;
      if (m?.overrides.wr$ !== undefined) wr$ = m.overrides.wr$;
      if (m?.overrides.te$ !== undefined) te$ = m.overrides.te$;
    });
    // Probe seam for the aug14 upstream sweep. MODIFIERS["Punt QB SF"] already
    // set qb$ = 30 in the loop above, and `_sflexDials.puntQb$` IS 30, so this
    // re-assignment is a bit-identical no-op at the shipped default. It exists
    // so the sweep can pin the qb$ override per cell without editing
    // archetypes.js between cells — same seam rule as every other dial.
    if (modifiers.includes("Punt QB SF")) qb$ = _sflexDials.puntQb$;
    // D2 — raw RB-share shift, applied before renormalization so the scale
    // factor below is unchanged (see _demandDials). Balanced is a structural
    // no-op: its 2:3 re-split at :254-260 reads only the rb$+wr$ POOL, which a
    // transfer leaves alone, and overwrites both. Verified per cell in the probe.
    if (_demandDials.rbShift !== 0) {
      const mv = _demandDials.rbShift * wr$;
      wr$ -= mv; rb$ += mv;
    }
    const raw = qb$ + rb$ + wr$ + te$;
    // Reserve $4 for K/DEF when they exist, $0 when both are 0
    const kSlots = settings?.positions?.K ?? 1;
    const defSlots = settings?.positions?.DEF ?? 1;
    const reserve = (kSlots === 0 && defSlots === 0) ? 0 : 4;
    if (raw > 0) {
      const s = (budget - reserve) / raw;
      qb$ = Math.round(qb$ * s); rb$ = Math.round(rb$ * s); wr$ = Math.round(wr$ * s);
      te$ = (budget - reserve) - qb$ - rb$ - wr$;
    }
    // FLEX budget boost: extra FLEX slots beyond 1 need real starter money
    const flexCount = settings?.positions?.FLEX || 1;
    if (flexCount > 1) {
      const flexExtra = (flexCount - 1) * 13;
      const isRBHeavy = archKey === "RB Heavy" || archKey === "Hero RB";
      const isWRHeavy = archKey === "WR Heavy" || archKey === "Zero RB";
      const rbShare = isRBHeavy ? 0.65 : isWRHeavy ? 0.35 : 0.50;
      const wrShare = 1.0 - rbShare;
      rb$ += Math.round(flexExtra * rbShare);
      wr$ += Math.round(flexExtra * wrShare);
      // Re-normalize to fit within budget
      const total = qb$ + rb$ + wr$ + te$;
      if (total > budget - reserve) {
        const scale = (budget - reserve) / total;
        qb$ = Math.round(qb$ * scale); rb$ = Math.round(rb$ * scale); wr$ = Math.round(wr$ * scale);
        te$ = (budget - reserve) - qb$ - rb$ - wr$;
      }
    }
    // Balanced: roster-aware RB/WR split — divide pool proportionally by starter slot counts
    // e.g. 2RB/3WR: pool=156 → rb$=62, wr$=94 instead of 78/78
    if (archKey === "Balanced") {
      const rbSlots = settings?.positions?.RB || 2;
      const wrSlots = settings?.positions?.WR || 3;
      const rwPool = rb$ + wr$;
      rb$ = Math.round(rwPool * rbSlots / (rbSlots + wrSlots));
      wr$ = rwPool - rb$;
    }
    const profile = {
      qb$, rb$, wr$, te$, archetype: archKey, selfNomRate: arch.selfNomRate,
      overpay: cfg.overpay ?? arch.overpay, poisonRate: arch.poisonRate,
      starterOverrides: arch.starterOverrides || {}, flexPos: arch.flexPos || null,
      starsMode: !!arch.starsMode, hammerMode: !!arch.hammerMode, rbCap: arch.rbCap || 999,
      rbSpendCap: _demandDials.rscScale === 1
        ? (arch.rbSpendCap || 0)                                             // D1 identity
        : (Number.isFinite(_demandDials.rscScale)
            ? Math.round((arch.rbSpendCap || 0) * _demandDials.rscScale)
            : 0),                                                            // Infinity = gate removed
      heroRB: !!arch.heroRB, valueMode: !!arch.valueMode, enforcer: modifiers.includes("Enforcer"),
      favoriteTeam: modifiers.includes("Homer") ? (cfg.favoriteTeam || null) : null,
      modifiers, shoppingList: arch.shoppingList || null, flexReserve: arch.flexReserve || 0,
    };
    modifiers.forEach(mod => {
      const m = MODIFIERS[mod];
      if (m?.shoppingListOverrides && profile.shoppingList) {
        profile.shoppingList = JSON.parse(JSON.stringify(profile.shoppingList));
        for (const [key, override] of Object.entries(m.shoppingListOverrides)) {
          profile.shoppingList[key] = { ...profile.shoppingList[key], ...override };
        }
      }
    });
    if (i !== userSlot && modifiers.includes("Homer") && !profile.favoriteTeam) {
      profile.favoriteTeam = NFL_TEAMS[Math.floor(Math.random() * NFL_TEAMS.length)];
    }
    // The `qb$ >= 35` escape decided ROSTER LEGALITY off a BUDGET number: Punt
    // QB SF (qb$ 30 scaled) fell under it and kept the 1QB-shaped cap, which is
    // the whole of "100% of failing seats are punt seats" — a punt budget number
    // sitting under a threshold, not a punt strategy effect. With the superflex
    // targets floored at the starter requirement above, legality is a roster
    // question again, so every profile floors at QB + SFLEX and no threshold
    // decides it. 1QB keeps the original expression verbatim.
    const isQBPremium = profile.qb$ >= 35;
    // ✅ RESOLVED aug24 — the note that stood here said `useQbFix` was gated on
    // `sflexCount > 0`, so a TRUE-2QB league (QB:2, SFLEX:0) fell to the
    // else-branch and a seat with qb$ < 35 took `qbTargets[i]` — 1 for the first
    // 50% of slots, i.e. a maxQB of 1 in a format mandating two QB starters.
    // The gate now keys on `isQbPremiumFormat` (see `qbGate` above and
    // `_sflexDials.maxQbGatePremium`), which is the predicate-shaped repair that
    // note called for: no new constant, and `qbRequired` was already 2 for such
    // a roster. Measured on a corpus-modal 2QB config before shipping, which is
    // the other thing the note asked for — docs/data/qb2qb-ai-aug24.md, n=200:
    // maxQB-1 incidence 12.33% -> 0%, seats finishing under two QBs 8.88% -> 0%.
    // The else-branch below is still LIVE — it is the `maxQbGatePremium: false`
    // arm and the whole of the 1QB path — so it is not dead code.
    const maxQB = useQbFix
      ? Math.max(qbTargets[i] || 1, qbRequired)
      : (isQBPremium ? Math.max(2, qbTargets[i] || 1) : (qbTargets[i] || 1));
    const maxTE = teTargets[i] || 1;
    // `configKey` is the teamConfigs key this seat was built FROM, written the
    // same way the cfg lookup at :216 reads it. It exists because the opponent
    // pool is SHUFFLED (:152-157), so a config key lands on a different slot
    // every draft and no caller can recover the mapping from outside. Keepers
    // attach to a config key, so applyKeepers joins on this. `name` cannot serve:
    // it is customName when one is set, and the canonical rule may append
    // " (CPU)" to it (:233).
    teams[i] = { name: displayName, configKey: i === userSlot ? "__user" : ownerKey, profileKey: archKey, profile, roster: [], budget: budget, isUser: i === userSlot, maxDef: i === extraDefSlot ? 2 : 1, maxQB, maxTE, starsPurchased: 0, starPositions: {}, heroRBDrafted: false, homerReserve: profile.favoriteTeam ? Math.round(budget * 0.04) : 0 };
    if (i === userSlot && userTierOverrides && Object.keys(userTierOverrides).length > 0) {
      // Build user's tier sheet from their custom overrides
      const userSheet = new Map();
      for (const p of PLAYERS) {
        const rn = parseRankNum(getEffectivePosRank(p.name, p.posRank));
        if (rn >= 999) continue;
        const baseTierObj = getTierForRank(p.pos, rn);
        if (!baseTierObj) continue;
        const overrideKey = `${p.pos}:${p.name}`;
        userSheet.set(p.name, userTierOverrides[overrideKey] ?? baseTierObj.tier);
      }
      teams[i].tierSheet = userSheet;
    } else {
      teams[i].tierSheet = generatePersonalTierSheet(PLAYERS, Math.floor(Math.random() * 2147483647));
    }
    if (i !== userSlot) {
      const ts = teams[i].tierSheet;
      const mods = teams[i].profile?.modifiers || [];
      // Build per-player tier lookup for flexible pool filtering
      const playerTiers = []; // [{player, baseTier, personalTier}]
      const shiftedDown = []; // Personal tier > base tier: avoid candidates
      const neutral = [];     // Unchanged: avoid filler
      for (const p of PLAYERS) {
        if (p.pos === "DEF" || p.pos === "K") continue;
        const rn = parseRankNum(getEffectivePosRank(p.name, p.posRank));
        if (rn >= 999) continue;
        const baseTierObj = getTierForRank(p.pos, rn);
        if (!baseTierObj) continue;
        const baseTier = baseTierObj.tier;
        const personalTier = ts.get(p.name) || baseTier;
        playerTiers.push({ player: p, baseTier, personalTier });
        if (baseTier >= 2 && baseTier <= 5 && personalTier > baseTier) shiftedDown.push(p);
        else if (baseTier >= 2 && baseTier <= 5) neutral.push(p);
      }
      // Helper: filter playerTiers by position(s) and tier range
      const poolFilter = (positions, minTier, maxTier) =>
        playerTiers.filter(e => positions.includes(e.player.pos) && e.baseTier >= minTier && e.baseTier <= maxTier).map(e => e.player);
      // Helper: pick random from pool, excluding usedNames
      // Draftable-pool rule: My Guys AND Sleepers are drawn only from in-pool players
      // (My Guys are already in-pool; this stops Sleepers being assigned undraftable deep guys).
      const pickFrom = (pool, usedNames, boost) => {
        pool = pool.filter(p => isDraftableMemo(p));
        for (let attempt = 0; attempt < 200; attempt++) {
          if (pool.length === 0) return null;
          const p = pool[Math.floor(Math.random() * pool.length)];
          if (!usedNames.has(p.name)) {
            usedNames.add(p.name);
            return { name: p.name, boost };
          }
        }
        return null;
      };

      const myGuys = [];
      const sleepers = [];
      const usedNames = new Set();
      const isPuntQB = mods.includes("Punt QB") || mods.includes("Punt QB SF");
      const isEliteQB = mods.includes("Elite QB") || mods.includes("Elite QB SF");
      const isPuntTE = mods.includes("Punt TE");
      const isEliteTE = mods.includes("Elite TE");

      // === Per-position My Guys (1 per skill pos baseline) ===
      const MG_TIERS = { QB: [1, 4], RB: [1, 5], WR: [1, 5], TE: [1, 4] };
      const SL_TIERS = { QB: [5, 99], RB: [7, 99], WR: [7, 99], TE: [5, 99] };
      const SKILL_POS = ["QB", "RB", "WR", "TE"];
      for (const pos of SKILL_POS) {
        let skipMyGuy = false;
        let [mgMinTier, mgMaxTier] = MG_TIERS[pos];
        if (pos === "QB" && isPuntQB) skipMyGuy = true;
        if (pos === "QB" && isEliteQB) { mgMinTier = 1; mgMaxTier = 2; }
        if (pos === "TE" && isPuntTE) skipMyGuy = true;
        if (pos === "TE" && isEliteTE) { mgMinTier = 1; mgMaxTier = 2; }
        if (pos === "RB" && archKey === "Zero RB") skipMyGuy = true;

        if (!skipMyGuy) {
          const pool = poolFilter([pos], mgMinTier, mgMaxTier);
          const g = pickFrom(pool, usedNames, 1.20 + Math.random() * 0.10);
          if (g) myGuys.push(g);
        }
      }

      // === Archetype extras (My Guys) ===
      if (archKey === "RB Heavy") {
        const pool = poolFilter(["RB"], 1, 5);
        const g = pickFrom(pool, usedNames, 1.20 + Math.random() * 0.10);
        if (g) myGuys.push(g);
      }
      if (archKey === "Zero RB") {
        const wrPool = poolFilter(["WR"], 1, 5);
        const g = pickFrom(wrPool, usedNames, 1.20 + Math.random() * 0.10);
        if (g) myGuys.push(g);
      }

      // === Weighted Sleeper allocation (4 total) ===
      // Core: 1-2 RB + 1-2 WR sleepers always
      const slBoost = () => 1.75 + Math.random() * 0.25;
      const addSleeper = (pos) => {
        const [slMin, slMax] = SL_TIERS[pos];
        const pool = poolFilter([pos], slMin, slMax);
        const g = pickFrom(pool, usedNames, slBoost());
        if (g) sleepers.push(g);
      };
      // RB: 1 guaranteed
      addSleeper("RB");
      // WR: 1 guaranteed
      addSleeper("WR");
      // QB: only if Punt QB or 30% random
      const wantsQBSleeper = isPuntQB || Math.random() < 0.30;
      if (wantsQBSleeper) addSleeper("QB");
      // TE: only if Punt TE or 30% random
      const wantsTESleeper = isPuntTE || Math.random() < 0.30;
      if (wantsTESleeper) addSleeper("TE");
      // Fill remaining slots (target 4 total) with extra RB/WR.
      // Cap tries — the draftable pool is narrower now, so a position can run dry;
      // bail out rather than spin forever when no in-pool candidate remains.
      let sleeperTries = 0;
      while (sleepers.length < 4 && sleeperTries < 20) {
        sleeperTries++;
        addSleeper(Math.random() < 0.50 ? "RB" : "WR");
      }
      // Zero RB: extra RB Sleeper
      if (archKey === "Zero RB") {
        addSleeper("RB");
      }

      teams[i].myGuys = myGuys;
      teams[i].sleepers = sleepers;

      // Avoids: 2-3 from shifted-down + neutral, excluding myGuys/sleepers
      const avoidPool = [];
      shiftedDown.forEach(p => { avoidPool.push(p); avoidPool.push(p); avoidPool.push(p); });
      neutral.forEach(p => { avoidPool.push(p); });
      const avoidCount = 2 + Math.floor(Math.random() * 2);
      const avoidSet = new Set();
      const avoidNames = [];
      const avoidPosCounts = {};
      for (let attempt = 0; attempt < 200 && avoidNames.length < avoidCount; attempt++) {
        if (avoidPool.length === 0) break;
        const pick = avoidPool[Math.floor(Math.random() * avoidPool.length)];
        if (avoidSet.has(pick.name) || usedNames.has(pick.name)) continue;
        if ((avoidPosCounts[pick.pos] || 0) >= 2) continue;
        avoidSet.add(pick.name); avoidNames.push(pick.name);
        avoidPosCounts[pick.pos] = (avoidPosCounts[pick.pos] || 0) + 1;
      }
      teams[i].avoids = avoidNames;
    } else {
      teams[i].myGuys = [];
      teams[i].sleepers = [];
      teams[i].avoids = [];
    }

    // Generate per-agent CV noise map — unique per-player willingness multiplier
    if (i !== userSlot) {
      const noiseMap = {};
      for (const p of UNIQUE_PLAYERS) {
        if (p.pos === "DEF" || p.pos === "K") continue;
        const tv = p.value || 1;
        // Higher CV for cheap players, lower for elite — mirrors real auction variance
        const cv = 0.20 + 0.55 * (1 - Math.min(tv, 55) / 55);
        // Box-Muller truncated normal
        const u1 = Math.random() || 0.0001;
        const u2 = Math.random();
        const raw = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const noise = Math.max(-1.5, Math.min(1.5, raw));
        noiseMap[p.name] = Math.max(0.75, Math.min(1.35, 1.0 + cv * noise));
      }
      teams[i].noiseMap = noiseMap;
    }
  }

  return teams;
}

// ── Keepers: seed a built team object with this year's kept players ──
//
// ONE function does ALL of it, and nothing else is allowed to seed half. That
// is TRAP 2, and it is not hypothetical:
//
//   roster seeded, drafted-set not  -> `while (draftedPlayers.size < totalPicks)`
//     wants totalPicks entries in the set while rosters are already partly
//     full. Teams hit `roster.length >= rosterSize`, get skipped as nominators,
//     the pool drains, and the loop hangs.
//   drafted-set seeded, roster not  -> budgets and rosters disagree. Every AI
//     bids as though it has a full roster to fill and a full budget, and the
//     kept player is silently missing from the results screen.
//
// So the four writes per kept player happen together or not at all, and the
// caller receives the kept-name Set to seed the drafted-set with — this function
// never reaches into anyone's Set.
//
// WHY THIS IS CHEAP. Almost all of "derived team state" is genuinely derived:
// posCount (auction.js:338), posSpent (:656) and emptySlots (`rosterSize -
// roster.length`) are all recomputed from `team.roster` on every call, so
// pushing {player, price} makes them correct for free. Only four fields are
// written rather than derived, and they are mirrored from the `--- PROCESS SOLD
// ---` block in simulateDraftSync below (~:964-981) rather than re-derived — if
// those conditions ever diverge, keeper teams and drafting teams behave
// differently. Its live-path twin is useMockDraft.js ~:1185-1196.
//
// ADDENDUM B — THE NO-OP INVARIANT. With no keepers this function consumes ZERO
// RNG draws and perturbs NO iteration order. There is no Math.random() in it at
// all, and the call site skips it entirely when there is nothing to seed. That
// is what makes the zero-keeper league byte-identical, provable by digest.
//
// DEFERRED, on purpose: profile.qb$/rb$/wr$/te$ are computed from the FULL
// budget before the team object exists (:287-339) and stay that way. A team that
// kept a $50 RB carries an rb$ target near $70 with posSpent of $50, so it has
// ~$20 of RB appetite left — which is the behavior you want, for free. What it
// does NOT do is re-derive the archetype's dollar targets against the smaller
// budget. That is deferred item 5 (per-team budget scaling) and must not be
// "fixed" here; it is a calibration project, not a feature.
//
// `resolvedByOwner` is { [configKey]: [{player, price}] } from
// utils/keepers.js `resolveKeepers`. Returns:
//   keptNames — Set of names for the caller's drafted-set
//   applied   — how many keepers were actually seeded
//   rejected  — [{owner, name, price, reason}] records refused at the engine
//               boundary. Never clamped: a silently clamped keeper price is the
//               failure mode that looks like the feature working. The UI blocks
//               these cases up front (validateKeepers); this is the backstop
//               that keeps a bad config from producing a negative budget.
export function applyKeepers(teams, resolvedByOwner, rosterSize) {
  const keptNames = new Set();
  const rejected = [];
  let applied = 0;
  if (!teams || !resolvedByOwner) return { keptNames, applied, rejected };

  // configKey -> seat. Built from the teams object, so a keeper on a config key
  // that never got a seat this draft (the opponent pool is teamCount-1 long, and
  // the canonical rule drops the last name) is REPORTED rather than invisible.
  const seatOf = new Map();
  for (const t of Object.values(teams)) if (t && t.configKey) seatOf.set(t.configKey, t);

  for (const owner of Object.keys(resolvedByOwner)) {
    const list = resolvedByOwner[owner] || [];
    const team = seatOf.get(owner);
    if (!team) {
      for (const k of list) rejected.push({ owner, name: k.player.name, price: k.price, reason: "no seat" });
      continue;
    }
    for (const { player, price } of list) {
      // One player, one seat. The drafted-set is keyed by name, so a second copy
      // would be swallowed silently — validateKeepers rejects this in the UI, and
      // this is what stops it corrupting a roster if it ever gets past.
      if (keptNames.has(player.name)) {
        rejected.push({ owner, name: player.name, price, reason: "already kept" });
        continue;
      }
      if (team.roster.length >= rosterSize) {
        rejected.push({ owner, name: player.name, price, reason: "roster full" });
        continue;
      }
      // The seat must still be able to fill every remaining slot at $1. Same
      // rule validateKeepers enforces, applied incrementally.
      const slotsAfter = rosterSize - team.roster.length - 1;
      if (price > team.budget - slotsAfter) {
        rejected.push({ owner, name: player.name, price, reason: "cannot fill remaining slots" });
        continue;
      }

      team.roster.push({ player, price });
      team.budget -= price;
      keptNames.add(player.name);
      applied++;

      // ── derived state, mirroring `--- PROCESS SOLD ---` (~:964-981) ──
      // NOTE THE ASYMMETRY, and preserve it: the stars test gates on PRICE
      // (>= 40), the heroRB test gates on player.value (>= 40). That is not a
      // typo — it is what the sold block's ~:969 and ~:975 actually do.
      //
      // starsPurchased/starPositions have no read site anywhere in src/ outside
      // src/tools/ today (grepped Aug 9). They are seeded anyway so the state is
      // honest whenever a read site appears; do not expect seeding them to change
      // any behavior. heroRBDrafted is the one that actually moves bidding —
      // auction.js:557, :960, :1726 — and it is why a seat that kept a $60 RB
      // must not go and buy another one.
      if (team.profile?.starsMode && price >= 40) {
        team.starsPurchased = (team.starsPurchased || 0) + 1;
        if (!team.starPositions) team.starPositions = {};
        team.starPositions[player.pos] = (team.starPositions[player.pos] || 0) + 1;
      }
      if (team.profile?.heroRB && player.pos === "RB" && player.value >= 40 && !team.heroRBDrafted) {
        team.heroRBDrafted = true;
      }
      if (team.homerReserve > 0 && team.profile?.favoriteTeam && player.team === team.profile.favoriteTeam) {
        team.homerReserve = Math.max(0, team.homerReserve - price);
      }
    }
  }

  return { keptNames, applied, rejected };
}

// ── SEAM B (_qbTail.benchQbBid) — bench-QB demand. OFF BY DEFAULT ───
//
// Returns 0 — "this seat is out, exactly as it is at HEAD" — at the default dial
// state, in 1QB, off QB, for a backup-class QB, and for any seat with no bench
// slack or no money. It is called ONLY from the bidder-selection gate below, in
// the branch that already decided to drop the seat.
//
// WHAT IT IS FOR. The gate is a roster-LEGALITY test doing WILLINGNESS work: it
// cannot tell "cannot roster another QB" from "does not want one at this price",
// and because it fires before calcAiMaxBidV2 is called at all, no bid-side seam
// can reach the seats it removes. Six of twelve superflex seats carry maxQB 2,
// so half the room leaves the QB market permanently the moment it fills QB +
// SFLEX (docs/data/qbtail-chunk1-aug20.md §1a). Those seats mostly act as a
// PRICE FLOOR here — second-price resolution means a capped bid usually raises
// `secondMax` rather than winning — which is precisely what P1 says is missing.
//
// STARTER-CLASS is settings-derived and never a name list: board posRank <=
// qbDemand = ((positions.QB || 1) + sflexCount) x teams, the same expression
// auction.js:530 computes. A backup-class QB gets nothing from this seam.
//
// THE CAP IS PROVISIONAL: min(0.35 x sticker, $6). The $6 is written on $200 in
// the brief and is scaled by budget here — an unscaled dollar threshold is this
// project's most common bug class, and it is the exact shape of the defect the
// aug20 read found in the two universal price floors (both gated on absolute
// dollars no QB can reach). Sticker is `dynamicPrices`, the same number the AI
// bids against and the user sees.
//
// The seat's own dampening is NOT re-applied on top: calcAiMaxBidV2 returns 0
// for a seat at its maxQB by construction (priceEnforceOnly, auction.js:483,
// whose only escape needs tierValue >= 30 — a tail QB draws $2-9), so this cap
// IS the bid rather than a bound on a larger one.
const QBTAIL_BENCH_FRAC = 0.35;
const QBTAIL_BENCH_CAP200 = 6;

function qbTailBenchBid(team, nominated, settings, sflexCount, rosterSize, priceMap, currentBid) {
  if (!_qbTail.benchQbBid || sflexCount <= 0) return 0;
  if (nominated.pos !== "QB") return 0;
  const P = settings?.positions || {};
  const qbDemand = ((P.QB || 1) + sflexCount) * (settings?.teams || 10);
  const rank = parseRankNum(getEffectivePosRank(nominated.name, nominated.posRank));
  if (!(rank <= qbDemand)) return 0;                      // starter-class only
  const emptySlots = rosterSize - team.roster.length;
  if (emptySlots <= 0) return 0;
  // "Open bench slot" = a slot beyond what this seat still owes its starters,
  // FLEX included. Same shape as the starter lockout at auction.js:828-839.
  const counts = {};
  team.roster.forEach(r => { counts[r.player.pos] = (counts[r.player.pos] || 0) + 1; });
  const reqs = { QB: (P.QB || 1) + sflexCount, RB: P.RB ?? 2, WR: P.WR ?? 3,
                 TE: P.TE ?? 1, DEF: P.DEF ?? 1, K: P.K ?? 1 };
  let unfilled = 0;
  for (const [p, req] of Object.entries(reqs)) unfilled += Math.max(0, req - (counts[p] || 0));
  const flexSlots = P.FLEX || 0;
  if (flexSlots > 0) {
    let flexFilled = 0;
    for (const fp of ["RB", "WR", "TE"]) flexFilled += Math.max(0, (counts[fp] || 0) - (reqs[fp] || 0));
    unfilled += Math.max(0, flexSlots - flexFilled);
  }
  if (emptySlots - unfilled < 1) return 0;                // no bench slack
  const sticker = priceMap?.get(`${nominated.name}-QB`) ?? nominated.value ?? 0;
  if (sticker <= 0) return 0;
  const cap = Math.min(QBTAIL_BENCH_FRAC * sticker,
                       QBTAIL_BENCH_CAP200 * ((settings?.budget || 200) / 200));
  const hardCap = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
  const bidCap = Math.min(Math.round(cap), hardCap, team.budget);
  return bidCap > currentBid ? bidCap : 0;
}

export function simulateDraftSync(initialTeams, startPick, startNomIdx, initialDrafted, initialLog, userSlot, sflexCount = 0, settings = {}, dynamicPriceMap = null, diagnosticLog = null, aiRosterSize = MOCK_ROSTER_SIZE) {
  // Deep clone teams so we can mutate freely
  const teams = {};
  for (const [id, t] of Object.entries(initialTeams)) {
    teams[id] = { ...t, roster: [...t.roster], starPositions: { ...(t.starPositions || {}) } };
  }
  const teamCount = Object.keys(teams).length;
  // True roster size from league settings (falls back to the legacy constant).
  // Fixes the hard-stop-at-16 bug so leagues >16 slots (e.g. FFPC=20) fill fully.
  const rosterSize = (settings && settings.positions ? buildRosterSlots(settings.positions).length : 0) || MOCK_ROSTER_SIZE;
  const draftedPlayers = new Set(initialDrafted);
  const draftLog = [...initialLog]; // Caller passes chronological order
  let pickNumber = startPick;
  let nominatorIndex = startNomIdx;
  const tierAnchors = {};
  let inflation = 0;
  const totalPicks = rosterSize * teamCount;
  const rd3BidDiag = [];
  let budgetSnapshot = null;

  while (draftedPlayers.size < totalPicks) {
    // --- NOMINATION ---
    const nomTeam = teams[nominatorIndex];
    if (!nomTeam || nomTeam.roster.length >= rosterSize) {
      nominatorIndex = (nominatorIndex + 1) % teamCount;
      continue;
    }
    const profile = nomTeam.profile;
    const tPosCounts = {};
    nomTeam.roster.forEach(p => { tPosCounts[p.player.pos] = (tPosCounts[p.player.pos] || 0) + 1; });
    let available = UNIQUE_PLAYERS.filter(p => !draftedPlayers.has(p.name)).filter(p => {
      if (p.pos === "K" && (settings.positions?.K === 0)) return false;
      if (p.pos === "K" && (tPosCounts.K || 0) >= 1) return false;
      if (p.pos === "DEF" && (settings.positions?.DEF === 0)) return false;
      if (p.pos === "DEF" && (tPosCounts.DEF || 0) >= (nomTeam.maxDef || 1)) return false;
      if (p.pos === "QB" && (tPosCounts.QB || 0) >= (nomTeam.maxQB || 2)) return false;
      if (p.pos === "TE" && (tPosCounts.TE || 0) >= (nomTeam.maxTE || 2)) return false;
      return true;
    });
    if (available.length === 0) {
      available = UNIQUE_PLAYERS.filter(p => !draftedPlayers.has(p.name));
      if (available.length === 0) break;
    }
    // Early pick constraints
    if (pickNumber <= teamCount) {
      const earlyPosCounts = {};
      draftLog.forEach(l => { earlyPosCounts[l.player.pos] = (earlyPosCounts[l.player.pos] || 0) + 1; });
      const picksLeft = teamCount - pickNumber + 1;
      if ((earlyPosCounts.QB || 0) >= 2) available = available.filter(p => p.pos !== "QB");
      if ((earlyPosCounts.TE || 0) >= 1) available = available.filter(p => p.pos !== "TE");
      const forcePos = new Set();
      if (5 - (earlyPosCounts.RB || 0) >= picksLeft) forcePos.add("RB");
      if (2 - (earlyPosCounts.WR || 0) >= picksLeft) forcePos.add("WR");
      if (forcePos.size > 0 && available.some(p => forcePos.has(p.pos))) {
        available = available.filter(p => forcePos.has(p.pos));
      }
    }
    let nominated = profile ? pickAiNomination(profile, nomTeam.roster, available, pickNumber, nomTeam, teams, sflexCount, settings, aiRosterSize) : null;
    if (!nominated) nominated = available.sort((a, b) => b.value - a.value)[0];
    if (!nominated) break;

    // pickAiNomination sets autoBuyPrice by mutating the shared UNIQUE_PLAYERS
    // object and never clears it, so a stale value would leak into later drafts
    // in the same process. Read it into a local and immediately delete the
    // property so every nomination path starts clean.
    const autoBuyPrice = nominated.autoBuyPrice;
    delete nominated.autoBuyPrice;

    // Opening bid — auto-buy: if pickAiNomination found a guaranteed steal, open at opponent max
    const nomEmpty = rosterSize - nomTeam.roster.length;
    const nomMax = nomEmpty <= 1 ? nomTeam.budget : nomTeam.budget - (nomEmpty - 1);
    let bid;
    if (autoBuyPrice != null) {
      bid = Math.max(1, Math.min(autoBuyPrice, nomMax));
    } else {
      const isMid = pickNumber > 50 && pickNumber <= 80;
      const isSelfBuy = profile ? (pickNumber > 80 ? true : Math.random() < (profile.selfNomRate + (isMid ? 0.2 : 0))) : false;
      bid = profile ? calcAiOpeningBid(profile, nominated, nomTeam.roster, nomTeam.budget, nomEmpty, isSelfBuy, nomTeam, sflexCount, settings, dynamicPriceMap, aiRosterSize) : 1;
      bid = Math.max(1, Math.min(bid, nomMax));
    }
    // Nominator archetype hard caps
    if (profile) {
      if (profile.starsMode) { const sc = nomTeam.roster.filter(p => p.price >= 40).length; if (sc >= 3) bid = Math.min(bid, 3); }
      if (profile.rbCap < 999 && nominated.pos === "RB") bid = Math.min(bid, profile.rbCap);
      if (profile.heroRB && nominated.pos === "RB" && nomTeam.heroRBDrafted) bid = Math.min(bid, 5);
      if (profile.rbSpendCap > 0 && nominated.pos === "RB") {
        const rbS = nomTeam.roster.filter(p => p.player.pos === "RB").reduce((s, p) => s + p.price, 0);
        bid = Math.min(bid, Math.max(1, profile.rbSpendCap - rbS));
      }
      if (profile.archetype === "Balanced") bid = Math.min(bid, 40);
    }

    // --- BIDDER SELECTION (replicates mockNominatePlayer lines 3228-3360) ---
    const bidders = new Set();
    // Seam B's admitted seats: id -> the capped bid. Empty at the default dial
    // state and in every 1QB room, in which case the block below is the shipped
    // `return` verbatim.
    const benchQbBids = new Map();
    Object.entries(teams).forEach(([id, team]) => {
      const i = parseInt(id);
      const emptySlots = rosterSize - team.roster.length;
      if (emptySlots <= 0 || team.budget < bid) return;
      const pCount = team.roster.filter(p => p.player.pos === nominated.pos).length;
      if (nominated.pos === "K" && pCount >= 1) return;
      if (nominated.pos === "DEF" && pCount >= (team.maxDef || 1)) return;
      if (nominated.pos === "QB" && pCount >= (team.maxQB || 2)) {
        const bq = qbTailBenchBid(team, nominated, settings, sflexCount, rosterSize, dynamicPriceMap, bid);
        if (bq > 0) { benchQbBids.set(i, bq); bidders.add(i); }
        return;
      }
      if (nominated.pos === "TE" && pCount >= (team.maxTE || 2)) return;
      bidders.add(i);
    });
    // V2: all teams bid, willingness handles filtering
    const pos = nominated.pos;

    // --- AUCTION RESOLUTION (second-price logic) ---
    const eliteCount = draftLog.filter(l => l.price >= 50).length;
    const willingBidders = [];
    bidders.forEach(id => {
      if (id === nominatorIndex) return;
      const team = teams[id];
      if (!team?.profile) return;
      // Seam B seat — bid the cap and draw no randomness. It is empty unless the
      // seam is on, so this branch never fires at the default dial state.
      const benchQb = benchQbBids.get(id);
      if (benchQb !== undefined) {
        if (benchQb > bid) willingBidders.push({ id, maxBid: benchQb, benchQb: true });
        return;
      }
      const emptySlots = rosterSize - team.roster.length;
      const teamCaps = { maxDef: team.maxDef, maxQB: team.maxQB, maxTE: team.maxTE };
      const maxBid = calcAiMaxBidV2(team.profile, nominated, team.roster, team.budget, emptySlots, eliteCount, teamCaps, team, pickNumber, tierAnchors, inflation, teams, sflexCount, settings, dynamicPriceMap, draftedPlayers, nominatorIndex, aiRosterSize);
      if (maxBid > bid) willingBidders.push({ id, maxBid });
    });
    // Value floor: all teams opportunistically bid on severely underpriced players
    // dynamicPriceMap is keyed `${name}-${pos}` (see usePricing.js) — a bare-name
    // lookup always missed, leaving this path dead until now.
    const dynPrice = dynamicPriceMap?.get(`${nominated.name}-${pos}`) ?? 0;
    if (dynPrice >= 10 && bid < dynPrice * 0.55) {
      Object.entries(teams).forEach(([id, team]) => {
        const i = parseInt(id);
        if (i === nominatorIndex) return;
        if (!team?.profile) return;
        const emptySlots = rosterSize - team.roster.length;
        if (emptySlots <= 0 || team.budget < bid + 1) return;
        if (Math.random() < 0.15) return; // 15% skip for variance
        const hardCap = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
        let valueBid = Math.round(dynPrice * (0.60 + Math.random() * 0.15));
        valueBid = Math.min(valueBid, hardCap, team.budget);
        if (valueBid > bid) {
          const existing = willingBidders.find(wb => wb.id === i);
          if (existing) { if (valueBid > existing.maxBid) existing.maxBid = valueBid; }
          else willingBidders.push({ id: i, maxBid: valueBid });
        }
      });
    }
    // Enforcer logic
    const enforcerCaught = Math.random() < 0.18;
    if (nominated.value >= 20 && bid < nominated.value * 0.60) {
      Object.entries(teams).forEach(([id, team]) => {
        const i = parseInt(id);
        if (i === nominatorIndex) return;
        const tp = team.profile;
        if (!tp || !tp.enforcer) return;
        if (tp.starsMode) {
          const sPicks = team.roster.filter(p => p.price >= 40);
          if (sPicks.length >= 3) return;
          const sPosCounts = {};
          sPicks.forEach(p => { sPosCounts[p.player.pos] = (sPosCounts[p.player.pos] || 0) + 1; });
          if ((sPosCounts[nominated.pos] || 0) >= 2) return;
        }
        const emptySlots = rosterSize - team.roster.length;
        if (emptySlots <= 0 || team.budget < bid + 1) return;
        const hardCap = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
        const eBudget = team.budget;
        const budgetFactor = eBudget > 100 ? 1.0 : eBudget >= 60 ? 0.7 : eBudget >= 30 ? 0.3 : 0.0;
        if (budgetFactor === 0) return;
        const eStarterReqs = { QB: 1, RB: 2, WR: 2, TE: 1, DEF: 1, K: 1 };
        Object.entries(tp.starterOverrides || {}).forEach(([p, c]) => { eStarterReqs[p] = c; });
        const ePosCnts = {};
        team.roster.forEach(r => { ePosCnts[r.player.pos] = (ePosCnts[r.player.pos] || 0) + 1; });
        let openStarters = 0;
        for (const [p, req] of Object.entries(eStarterReqs)) openStarters += Math.max(0, req - (ePosCnts[p] || 0));
        const slotsFactor = openStarters >= 4 ? 1.0 : openStarters >= 2 ? 0.5 : 0.1;
        const enforcementWillingness = 0.75 * budgetFactor * slotsFactor;
        if (Math.random() > enforcementWillingness) return;
        let phaseMaxBid = Infinity;
        if (pickNumber > 70) phaseMaxBid = 3;
        else if (pickNumber > 30) phaseMaxBid = Math.round(nominated.value * 0.45);
        let reserveForTargets = 0;
        if (tp.shoppingList) {
          for (const [slotKey, slotDef] of Object.entries(tp.shoppingList)) {
            const slotPos = slotDef.pos || slotKey.replace(/[0-9]/g, '');
            if (slotPos === "ANY" || slotPos === "STAR") continue;
            const slotNum = parseInt(slotKey.replace(/[A-Z]+/g, ''), 10) || 1;
            const filled = ePosCnts[slotPos] || 0;
            if (slotKey === "FLEX") { const sc = eStarterReqs[slotPos] || 2; if (filled > sc) continue; }
            else if (filled >= slotNum) continue;
            reserveForTargets += slotDef.maxBid[0];
          }
        }
        const filledSlots = team.roster.length;
        const totalStarters = Object.values(eStarterReqs).reduce((s, v) => s + v, 0);
        const benchSlots = Math.max(0, rosterSize - totalStarters);
        const filledBench = Math.max(0, filledSlots - totalStarters);
        reserveForTargets += Math.max(0, benchSlots - filledBench);
        const reserveCap = Math.max(0, eBudget - reserveForTargets);
        const enforcerMax = enforcerCaught ? Math.round(nominated.value * 0.72) : Math.round(nominated.value * 0.55);
        // Position-aware: if team already hit budget target, bid full strength but can't win (price pressure only)
        const ePosBudgetKey = (nominated.pos === "DEF" || nominated.pos === "K") ? null : nominated.pos.toLowerCase() + "$";
        let enforcerPriceOnly = false;
        if (ePosBudgetKey) {
          const ePosSpent = team.roster.filter(p => p.player.pos === nominated.pos).reduce((s, p) => s + p.price, 0);
          if ((tp[ePosBudgetKey] || 0) > 0 && ePosSpent >= (tp[ePosBudgetKey] || 0)) {
            enforcerPriceOnly = true;
          }
        }
        let maxBid = Math.min(enforcerMax, hardCap, phaseMaxBid, reserveCap);
        const ePos = nominated.pos;
        if (tp.heroRB && ePos === "RB" && team.heroRBDrafted) maxBid = Math.min(maxBid, 5);
        if (tp.rbCap < 999 && ePos === "RB") maxBid = Math.min(maxBid, tp.rbCap);
        if (tp.rbSpendCap > 0 && ePos === "RB") {
          const eRbSpent = team.roster.filter(p => p.player.pos === "RB").reduce((s, p) => s + p.price, 0);
          if (eRbSpent >= tp.rbSpendCap) maxBid = 0;
          else maxBid = Math.min(maxBid, tp.rbSpendCap - eRbSpent);
        }
        const ePosKey = (ePos === "DEF" || ePos === "K") ? null : ePos.toLowerCase() + "$";
        if (tp.favoriteTeam && nominated.team === tp.favoriteTeam && ePosKey) {
          const eSpent = team.roster.filter(p => p.player.pos === ePos).reduce((s, p) => s + p.price, 0);
          const eTarget = tp[ePosKey] || 0;
          if (eTarget > 0 && eSpent >= eTarget + 10) maxBid = 0;
          else if (eTarget > 0) maxBid = Math.min(maxBid, Math.max(0, eTarget + 10 - eSpent));
        }
        if (tp.archetype === "Balanced") maxBid = Math.min(maxBid, 40);
        if (maxBid <= bid) return;
        const existing = willingBidders.find(wb => wb.id === i);
        if (existing) { if (maxBid > existing.maxBid) existing.maxBid = maxBid; }
        else willingBidders.push({ id: i, maxBid, priceOnly: enforcerPriceOnly });
      });
    }
    applyValueRecognitionFloor({
      player: nominated, currentBid: bid, excludeIndex: nominatorIndex,
      willingBidders, teams, rosterSize, priceMap: dynamicPriceMap, budget: settings?.budget ?? 200,
      recheckMaxBid: (team, emptySlots, teamCaps) => calcAiMaxBidV2(
        team.profile, nominated, team.roster, team.budget, emptySlots, eliteCount, teamCaps,
        team, pickNumber, tierAnchors, inflation, teams, sflexCount, settings,
        dynamicPriceMap, draftedPlayers, nominatorIndex, aiRosterSize),
    });

    // Second-price resolution
    let winnerId = nominatorIndex;
    let winPrice = bid;
    if (willingBidders.length > 0) {
      let highestMax = 0, highestId = -1, secondMax = bid;
      willingBidders.forEach(({ id, maxBid, priceOnly }) => {
        if (!priceOnly) {
          if (maxBid > highestMax) { secondMax = Math.max(secondMax, highestMax); highestMax = maxBid; highestId = id; }
          else if (maxBid > secondMax) secondMax = maxBid;
        } else {
          if (maxBid > secondMax) secondMax = maxBid; // push price, can't win
        }
      });
      if (highestId !== -1) {
        winnerId = highestId;
        winPrice = Math.min(Math.max(bid, secondMax + 1), highestMax);
      } // else: only priceOnly bidders — nominator wins at current bid
    }

    // Bid-trace note — pre-sale snapshot of this nomination (diagnostic; off by
    // default). Placed before PROCESS SOLD so team state is the state every
    // calcAiMaxBidV2 call above actually saw.
    if (_traceWants(nominated.name)) {
      _traceNote({
        pick: pickNumber, player: nominated.name, pos: nominated.pos,
        openingBid: bid, autoBuyPrice: autoBuyPrice ?? null,
        nominatorId: nominatorIndex, nominator: nomTeam?.name,
        winnerId, winner: teams[winnerId]?.name, price: winPrice,
        eliteCount, inflation,
        boardPrice: dynamicPriceMap?.get(`${nominated.name}-${nominated.pos}`) ?? null,
        inBidderSet: [...bidders],
        willing: willingBidders.map(w => ({ id: w.id, name: teams[w.id]?.name, maxBid: w.maxBid, priceOnly: !!w.priceOnly })),
        teamState: Object.entries(teams).map(([id, t]) => {
          const cnt = {}; const spend = {};
          t.roster.forEach(r => { cnt[r.player.pos] = (cnt[r.player.pos] || 0) + 1; spend[r.player.pos] = (spend[r.player.pos] || 0) + r.price; });
          return { id: parseInt(id), name: t.name, archetype: t.profileKey,
                   modifiers: t.profile?.modifiers || [], budget: t.budget,
                   slotsLeft: rosterSize - t.roster.length, posCount: cnt, posSpend: spend,
                   heroRBDrafted: !!t.heroRBDrafted,
                   rb$: t.profile?.rb$, rbCap: t.profile?.rbCap, rbSpendCap: t.profile?.rbSpendCap };
        }),
        rbMarket: draftLog.filter(l => l.player.pos === 'RB').map(l => ({ name: l.player.name, price: l.price, pick: l.pickNumber })),
      });
    }

    // Rd3 bid diagnostic (picks 32-47)
    if (pickNumber >= 32 && pickNumber <= 47) {
      const allBids = willingBidders.map(wb => wb.maxBid).sort((a, b) => b - a);
      allBids.unshift(bid); // include nominator's opening bid
      // de-dup and re-sort
      const sorted = [...new Set(allBids)].sort((a, b) => b - a);
      rd3BidDiag.push({
        pickNum: pickNumber,
        pos: nominated.pos,
        playerName: nominated.name,
        salePrice: winPrice,
        bids: sorted,
      });
    }

    // --- PROCESS SOLD ---
    const winTeam = teams[winnerId];
    winTeam.roster.push({ player: nominated, price: winPrice });
    winTeam.budget -= winPrice;
    // Stars & Scrubs tracking
    if (winTeam.profile?.starsMode && winPrice >= 40) {
      winTeam.starsPurchased = (winTeam.starsPurchased || 0) + 1;
      if (!winTeam.starPositions) winTeam.starPositions = {};
      winTeam.starPositions[nominated.pos] = (winTeam.starPositions[nominated.pos] || 0) + 1;
    }
    // Hero RB tracking
    if (winTeam.profile?.heroRB && nominated.pos === "RB" && nominated.value >= 40 && !winTeam.heroRBDrafted) {
      winTeam.heroRBDrafted = true;
    }
    // Homer reserve reduction
    if (winTeam.homerReserve > 0 && winTeam.profile?.favoriteTeam && nominated.team === winTeam.profile.favoriteTeam) {
      winTeam.homerReserve = Math.max(0, winTeam.homerReserve - winPrice);
    }
    // Tier anchors & inflation. The rank resolution and the mid live in
    // utils/roomTemp.js so this loop, the browser loop (useMockDraft.js) and
    // the Live Board cannot drift apart — they were three copies of the same
    // eight lines. UNITS (floor/ceiling are budget FRACTIONS, tiers.js:5) and
    // the jul26 history of getting that wrong are documented on tierMidDollars.
    const anchorTierObj = tierForPlayer(nominated);
    if (anchorTierObj) {
      const tier = anchorTierObj.tier;
      const tierMid = tierMidDollars(nominated, settings, dynamicPriceMap);
      if (winPrice >= tierMid * 0.5) {
        if (!tierAnchors[nominated.pos]) tierAnchors[nominated.pos] = {};
        if (tierAnchors[nominated.pos][tier] === undefined) tierAnchors[nominated.pos][tier] = winPrice;
      }
      inflation += (winPrice - tierMid);
    }
    // Draft log
    draftedPlayers.add(nominated.name);
    draftLog.push({
      pickNumber, player: nominated, price: winPrice, winnerId,
      winnerName: teams[winnerId]?.name || `Team ${winnerId + 1}`,
      nominatorId: nominatorIndex,
      nominatorName: teams[nominatorIndex]?.name || `Team ${nominatorIndex + 1}`,
      bidCount: willingBidders.filter(b => !b.priceOnly).length + 1,
    });
    // Diagnostic logging — compute every team's maxBid for this pick
    if (diagnosticLog) {
      const nomTierSheet = teams[nominatorIndex]?.tierSheet;
      const nomTier = nomTierSheet?.get(nominated.name) || (() => {
        const rn = parseRankNum(getEffectivePosRank(nominated.name, nominated.posRank));
        return rn < 999 ? (getTierForRank(nominated.pos, rn)?.tier || 99) : 99;
      })();
      const diagBids = Object.entries(teams).map(([id, t]) => {
        const i = parseInt(id);
        const es = rosterSize - t.roster.length;
        let mb = 0;
        if (t.profile && es > 0) {
          const tc = { maxDef: t.maxDef, maxQB: t.maxQB, maxTE: t.maxTE };
          mb = calcAiMaxBidV2(t.profile, nominated, t.roster, t.budget, es, eliteCount, tc, t, pickNumber, tierAnchors, inflation, teams, sflexCount, settings, dynamicPriceMap, draftedPlayers, nominatorIndex, aiRosterSize);
        }
        return {
          name: t.name, archetype: t.profileKey,
          modifiers: t.profile?.modifiers || [],
          maxBid: mb, budget: t.budget, emptySlots: es,
          isPatient: t.profile?.modifiers?.includes("Patient Spender") || false,
        };
      });
      diagnosticLog.push({
        pickNum: pickNumber, player: nominated.name, pos: nominated.pos,
        tier: nomTier, winner: teams[winnerId]?.name || `Team ${winnerId + 1}`,
        price: winPrice, bids: diagBids,
      });
    }
    // Capture budget snapshot at pick 50
    if (pickNumber === 50 && !budgetSnapshot) {
      budgetSnapshot = Object.values(teams).map(t => ({
        name: t.name,
        archetype: t.profileKey || 'Unknown',
        budget: t.budget,
        slots: rosterSize - t.roster.length,
      }));
    }
    // Advance nominator
    pickNumber++;
    let next = (nominatorIndex + 1) % teamCount;
    let safety = 0;
    while (safety < teamCount) {
      if (teams[next] && teams[next].roster.length < rosterSize) break;
      next = (next + 1) % teamCount;
      safety++;
    }
    nominatorIndex = next;
  }

  draftLog.reverse(); // Return newest-first to match mockDraftLog React state format
  return { teams, draftLog, draftedPlayers, pickNumber: pickNumber - 1, rd3BidDiag, budgetSnapshot };
}
